import React, { useEffect } from "react";
import { Spin } from "antd";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import { useLoadingTimeout } from "@/hooks/useLoadingTimeout";
import envConfig from "@/config/envConfig";

/**
 * Loading overlay component that shows a timeout toast after prolonged loading
 * Displays "This is taking a little longer than expected, please give us a moment."
 * after the specified timeout duration (configured in .env as VITE_LOADING_TIMEOUT_MS)
 */
const LoadingOverlay = ({ isLoading, children, className = "" }) => {
  const showTimeoutMessage = useLoadingTimeout(isLoading, envConfig.LOADING_TIMEOUT_MS);

  useEffect(() => {
    if (showTimeoutMessage && isLoading) {
      // Show toast notification when timeout is reached
      Swal.fire({
        icon: "info",
        title: "Processing...",
        text: "This is taking a little longer than expected, please give us a moment.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: false, // Keep showing until loading completes
        timerProgressBar: false,
        allowOutsideClick: true,
        allowEscapeKey: true,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    } else if (!isLoading && showTimeoutMessage) {
      // Close the toast when loading completes
      Swal.close();
    }
  }, [showTimeoutMessage, isLoading]);

  return (
    <Spin spinning={isLoading} className={className}>
      {children}
    </Spin>
  );
};

LoadingOverlay.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default LoadingOverlay;
